import { Transaction } from 'dexie';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import type { DocumentationEntity } from 'serviceWorker/repository/documentation/entity';
import type { LocalizedOnMap } from 'shared/domain/issue/issueLocation';
import { LogLevel } from 'shared/types/logger';
import { hasChangesToSend } from './documentations/documentationVersions';
import { isDocumentSettled } from './documentSettlement';
import {
  LegacyRequestContext,
  pickLegacyLevelMap,
  QueuedRequest,
  rewriteLegacyQueuedRequest,
} from './versioning/legacyQueuedRequests';
import { DocumentParentBond } from 'shared/domain/document/documentModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { HttpRequestModelType } from 'shared/domain/httpRequest/httpRequestModel';
import { DocumentInDto } from 'shared/dtos/in/document';
import { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import {
  DocumentEntity,
  DocumentInsertEntity,
} from 'serviceWorker/repository/document/entity';
import { inDtoToDocumentInsertEntity } from 'serviceWorker/repository/document/documentInDtoToEntity';
import { Identificable } from 'shared/types/commonView';

export const version15Store = {
  issues: `&_id, createdAt, createdBy, modifiedAt, modifiedBy, deleted, stage, process, hashtag`,
  issuesService: `++_id, total, isDownloading, isDownloaded, shouldReset, syncKey`,
  config: `++_id, locale, token, frontendVersion, api, projectId, timezone`,
  companies: `&_id, shortLabel, longLabel, deleted, createdAt, createdBy, modifiedAt, modifiedBy, sites, projectInvolvementType`,
  companiesService: `++_id, total, isDownloading, isDownloaded, shouldReset, syncKey`,
  contracts: `&_id, label, parties, createdBy, modifiedAt, modifiedBy`,
  contractsService: `++_id, total, isDownloading, isDownloaded, shouldReset, syncKey`,
  users: `&_id, label`,
  usersService: `++_id, total, isDownloading, isDownloaded, shouldReset`,
  permissions: `++_id, sites, processes, role`,
  analytics: `&_id, name, projectId, filters, series, locale, additionalParams, updatedAt`,
  chartFilters: `&_id, filters`,
  projects: `&_id, contractNumbers, name, sites, subcontractors, timezone`,
  projectsService: `++_id, total, isDownloading, isDownloaded, shouldReset`,
  selectedProject: `&_id`,
  inspectionTemplates: `&_id, checklist, generalNorm, process, status, summary, createdBy, createdAt, deleted, modifiedAt, modifiedBy`,
  inspectionTemplatesService: `++_id, total, isDownloading, isDownloaded, shouldReset, syncKey`,
  httpRequests: `++_id, method, url, data, headers, createdAt`,
  inspections: `&_id, template, workTypes, controlledParty, protocol, contracts, site, levels, isCompleted, summary, inspectionDate, comment, createdAt, deleted, modifiedAt, modifiedBy`,
  inspectionsService: `++_id, total, isDownloading, isDownloaded, shouldReset, syncKey`,
};
export const version15Upgrade = (
  transaction: Transaction
): Promise<any> => {
  return transaction
    .table('issues')
    .toCollection()
    .modify((issue) => {
      issue.deleted = issue.deleted ? 1 : 0;
    });
};

export const version18Store = {
  sites: `&_id, label`,
  sitesService: `++_id`,
  projects: `&_id, name`,
  issues: `&_id, deleted, stage`,
  issuesService: `++_id`,
  config: `++_id, locale, token, frontendVersion, api, projectId, timezone`,
  companies: `&_id`,
  companiesService: `++_id`,
  contracts: `&_id`,
  contractsService: `++_id`,
  users: `&_id, label`,
  usersService: `++_id`,
  permissions: `++_id`,
  analytics: `&_id, name, projectId, additionalParams, updatedAt`,
  chartFilters: `&_id, filters`,
  projectsService: `++_id`,
  selectedProject: `&_id`,
  inspectionTemplates: `&_id, deleted`,
  inspectionTemplatesService: `++_id`,
  httpRequests: `++_id, status`,
  inspections: `&_id, deleted`,
  inspectionsService: `++_id`,
  documents: `++localId, &_id, issueId, eventId, inspectionId, templateId, checklistId, sectionId, protocolId`,
};

export const version18Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  const documents: DocumentInsertEntity[] = [];
  const BINDINGS_BASE = {
    issueId: '',
    eventId: '',
    templateId: '',
    inspectionId: '',
    checklistId: '',
    sectionId: '',
    protocolId: '',
  };

  function inDtoToDocumentInsertEntity(
    documentInDto: DocumentInDto,
    parentEntityObject: DocumentParentBond
  ): any {
    return {
      ...BINDINGS_BASE,
      ...parentEntityObject,
      ...documentInDto,
    };
  }

  await transaction
    .table('issues')
    .toCollection()
    .modify((issue) => {
      const docs = [
        ...issue.primaryData.documents.map((document: DocumentInDto) =>
          inDtoToDocumentInsertEntity(document, {
            issueId: issue._id,
          })
        ),
        ...issue.events.flatMap((event: any) => {
          const eventDocs = event.documents.map(
            (document: DocumentInDto) =>
              inDtoToDocumentInsertEntity(document, {
                issueId: issue._id,
                eventId: event._id,
              })
          );
          return eventDocs;
        }),
      ];

      documents.push(...docs);
    });

  await transaction
    .table('inspections')
    .toCollection()
    .modify((inspection) => {
      const docs = inspection.protocol.flatMap(
        (protocolItem: ProtocolItemInDto) => {
          const documents =
            protocolItem.documents ??
            protocolItem.complianceCheck?.documents ??
            [];
          return documents.map(
            (document: DocumentInDto) =>
              inDtoToDocumentInsertEntity(document, {
                inspectionId: inspection._id,
                protocolId: protocolItem._id,
              })
          );
        }
      );
      documents.push(...docs);
    });

  await transaction
    .table('inspectionTemplates')
    .toCollection()
    .modify((template) => {
      const docs = [
        ...template.generalNorm.documents.map((document: DocumentInDto) =>
          inDtoToDocumentInsertEntity(document, {
            templateId: template._id,
          })
        ),
        ...template.checklist.flatMap((checklistItem: any) => {
          const checklistDocs = checklistItem.norm.documents.map(
            (document: DocumentInDto) =>
              inDtoToDocumentInsertEntity(document, {
                templateId: template._id,
                checklistId: checklistItem._id,
              })
          );
          return checklistDocs;
        }),
      ];

      documents.push(...docs);
    });

  await transaction.table('documents').bulkAdd(documents);
};

export const version23Store = {
  analytics: `&_id, name, projectId, additionalParams, updatedAt`,
  chartFilters: `&_id, filters`,
  companies: `&_id`,
  companiesService: `++_id`,
  config: `++_id, locale, token, frontendVersion, api, projectId, timezone`,
  contracts: `&_id`,
  contractsService: `++_id`,
  documents: `++localId, &_id, issueId, eventId, inspectionId, templateId, checklistId, sectionId, protocolId`,
  hazardCategories: `&_id, label, processes`,
  hazardCategoriesService: `++_id`,
  httpRequests: `++_id, status`,
  inspections: `&_id, deleted`,
  inspectionsService: `++_id`,
  inspectionTemplates: `&_id, deleted`,
  inspectionTemplatesService: `++_id`,
  issues: `&_id, deleted, stage`,
  issuesService: `++_id`,
  permissions: `++_id`,
  projects: `&_id, name`,
  projectsService: `++_id`,
  selectedProject: `&_id`,
  sites: `&_id, label`,
  sitesService: `++_id`,
  users: `&_id, label`,
  usersService: `++_id`,
  worktypes: `&_id, label, processes`,
  worktypesService: `++_id`,
  environmentalAspects: `&_id, label, processes`,
  environmentalAspectsService: `++_id`,
  correctiveActionTypes: `&_id, label, processes`,
  correctiveActionTypesService: `++_id`,
};
export const version23Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  const DOCUMENT_ENTITY_TYPES = [
    HttpRequestModelType.document,
    HttpRequestModelType.drawing,
  ];
  const httpRequests = await transaction
    .table('httpRequests')
    .toCollection()
    .toArray();

  await transaction
    .table('documents')
    .toCollection()
    .modify((document: DocumentEntity) => {
      const currentDocumentRequests = httpRequests.filter((request) => {
        return (
          DOCUMENT_ENTITY_TYPES.includes(request.entityType) &&
          request.data.localId === document.localId
        );
      });

      document.syncStatus = SyncStatus.SUCCESS;
      document.drawingSyncStatus = SyncStatus.SUCCESS;

      if (!currentDocumentRequests.length) {
        return;
      }

      currentDocumentRequests.forEach((documentRequest) => {
        if (documentRequest.entityType === HttpRequestModelType.document) {
          document.syncStatus = SyncStatus.PENDING;
        } else if (
          documentRequest.entityType === HttpRequestModelType.drawing
        ) {
          document.drawingSyncStatus = SyncStatus.PENDING;
        }
      });
    });
};

export const version28Store = {
  sites: `&_id, label, deleted`,
  levels: `&_id, label, site, deleted`,
  levelsService: `++_id`,
  documentations: `++localId, &_id, parentId, deleted, name`,
  documentationsService: `++_id`,
  users: `&_id, label, role`,
  directories: `++localId, &_id, parentId, deleted, name`,
  directoriesService: `++_id`,
};
export const version28Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('documentations')
    .toCollection()
    .modify((documentation: any) => {
      documentation.parentId = documentation.parentId
        ? documentation.parentId
        : '';
      documentation.deleted = documentation.deleted ? 1 : 0;
    });
};

function updateEntityAuthor(item: any): void {
  item.createdBy = item.createdBy?._id;
  item.modifiedBy = item.modifiedBy?._id;
}
export const version29Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('companies')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('contracts')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('directories')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('documentations')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('documents')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('inspectionTemplates')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('inspections')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('sites')
    .toCollection()
    .modify(updateEntityAuthor);

  await transaction
    .table('levels')
    .toCollection()
    .modify(updateEntityAuthor);
};

export const version30Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('directories')
    .toCollection()
    .modify((item: any) => {
      item.deleted = item.deleted ? 1 : 0;
    });
};

export const version31Store = {
  httpRequests: `++_id, status, method, entityType`,
};

export const version32Store = {
  companies: `&_id, deleted`,
  contracts: `&_id, deleted`,
};

export const version32Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('companies')
    .toCollection()
    .modify((item: any) => {
      item.deleted = item.deleted ? 1 : 0;
    });

  await transaction
    .table('contracts')
    .toCollection()
    .modify((item: any) => {
      item.deleted = item.deleted ? 1 : 0;
    });
};

export const version33Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('issues')
    .toCollection()
    .modify((item: any) => {
      item.primaryData.site = item.primaryData.site._id;
      item.primaryData.level = item.primaryData.level._id;
      item.primaryData.subcontractors =
        item.primaryData.subcontractors?.map(
          (element: Identificable) => element._id
        );
      item.primaryData.contractNumbers =
        item.primaryData.contractNumbers?.map(
          (element: Identificable) => element._id
        );

      item.extendedData.workTypes = item.extendedData.workTypes?.map(
        (element: Identificable) => element._id
      );
      item.extendedData.rootCauses = item.extendedData.rootCauses?.map(
        (element: Identificable) => element._id
      );
      item.extendedData.impact = item.extendedData.impact?._id;
      item.extendedData.environmentalAspect =
        item.extendedData.environmentalAspect?._id;
      item.extendedData.effect = item.extendedData.effect?._id;
      item.extendedData.hazardCategory =
        item.extendedData.hazardCategory?.map(
          (element: Identificable) => element._id
        );
      item.extendedData.subcontractorRepresentative =
        item.extendedData.subcontractorRepresentative?._id;
      item.extendedData.decisionToImposeFine =
        item.extendedData.decisionToImposeFine?._id;
      item.extendedData.circumstances =
        item.extendedData.circumstances?._id;
      item.extendedData.delayReason = item.extendedData.delayReason?._id;
      item.extendedData.rejectionReason =
        item.extendedData.rejectionReason?._id;
      item.extendedData.proposedCorrectiveAction =
        item.extendedData.proposedCorrectiveAction?._id;
      item.extendedData.spilledSubstance =
        item.extendedData.spilledSubstance?._id;
      item.extendedData.estimatedCost =
        item.extendedData.estimatedCost?.map(
          (cost: { coveredBy: Identificable }) => ({
            ...cost,
            coveredBy: cost.coveredBy?._id,
          })
        );
      item.extendedData.finalCost = item.extendedData.finalCost?.map(
        (cost: { coveredBy: Identificable }) => ({
          ...cost,
          coveredBy: cost.coveredBy?._id,
        })
      );
      item.process = item.process._id;

      item.events = item.events?.map(
        (event: { description: { type: 'mention'; user: any }[] }) => ({
          ...event,
          description:
            event.description?.map((descriptionPart) => {
              if (descriptionPart.type === 'mention') {
                descriptionPart.user = descriptionPart.user?._id;
              }
              return descriptionPart;
            }) || [],
        })
      );
    });

  await transaction
    .table('inspections')
    .toCollection()
    .modify((item: any) => {
      item.contracts = item.contracts?.map(
        (element: Identificable) => element._id
      );
      item.controlledParty = item.controlledParty?.map(
        (element: Identificable) => element._id
      );
      item.levels = item.levels?.map(
        (element: Identificable) => element._id
      );
      item.site = item.site?._id;
      item.workTypes = item.workTypes?.map(
        (element: Identificable) => element._id
      );
    });
};

export const version34Store = {
  issueForms: `&process`,
  issueFormsService: `++_id`,
};

export const version35Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('issues')
    .toCollection()
    .modify((item: any) => {
      const hasTargetAreaData =
        Array.isArray(item.primaryData.targetArea) &&
        item.primaryData.targetArea.length > 0;

      item.primaryData.targetAreas = hasTargetAreaData
        ? [[...item.primaryData.targetArea]]
        : [];

      const hasFinalAreaData =
        Array.isArray(item.primaryData.finalArea) &&
        item.primaryData.finalArea.length > 0;

      item.primaryData.finalAreas = hasFinalAreaData
        ? [[...item.primaryData.finalArea]]
        : [];

      delete item.primaryData.targetArea;
      delete item.primaryData.finalArea;
    });

  await transaction
    .table('httpRequests')
    .toCollection()
    .modify((item: any) => {
      // we specificly do not use HttpRequestModelTypes in migations, it might change in future.
      if (item.entityType === 'issue') {
        if (
          item.data.primaryData.targetArea &&
          Array.isArray(item.data.primaryData.targetArea) &&
          item.data.primaryData.targetArea.length
        ) {
          item.data.primaryData.targetAreas = [
            [...item.data.primaryData.targetArea],
          ];
          delete item.data.primaryData.targetArea;
        }

        if (
          item.data.primaryData.finalArea &&
          Array.isArray(item.data.primaryData.finalArea) &&
          item.data.primaryData.finalArea.length
        ) {
          item.data.primaryData.finalAreas = [
            [...item.data.primaryData.finalArea],
          ];
          delete item.data.primaryData.finalArea;
        }
      }
    });
};

export const version38Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction.table('chartFilters').clear();
};

export const version39Store = {
  organizations: `&_id, label`,
  organizationsService: `++_id`,
};

export const version40Store = {
  documents: `++localId, &_id, issueId, eventId, inspectionId, templateId, checklistId, sectionId, protocolId, incomingEmailMessageId`,
};

export const version40Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('documents')
    .toCollection()
    .modify((document: DocumentEntity) => {
      document.incomingEmailMessageId = '';
    });
};

export const version41Store = {
  issueVerificationItems: `&_id, label, processes`,
  issueVerificationItemsService: `++_id`,
  delayReasons: `&_id, label, processes`,
  delayReasonsService: `++_id`,
  rejectionReasons: `&_id, label, processes`,
  rejectionReasonsService: `++_id`,
};

export const version42Store = {
  rootCauses: `&_id, label, processes`,
  rootCausesService: `++_id`,
  effects: `&_id, label, processes`,
  effectsService: `++_id`,
  circumstances: `&_id, label, processes`,
  circumstancesService: `++_id`,
  impacts: `&_id, label, processes`,
  impactsService: `++_id`,
  decisionsToImposeFine: `&_id, label, processes`,
  decisionsToImposeFineService: `++_id`,
  spilledSubstances: `&_id, label, processes`,
  spilledSubstancesService: `++_id`,
};

export const version43Store = {
  priorities: `&_id, label, processes`,
  prioritiesService: `++_id`,
  projectStages: `&_id, label, processes`,
  projectStagesService: `++_id`,
};

export const version44Store = {
  employmentTypes: `&_id, label, processes`,
  employmentTypesService: `++_id`,
  injuryTypes: `&_id, label, processes`,
  injuryTypesService: `++_id`,
  injuredBodyParts: `&_id, label, processes`,
  injuredBodyPartsService: `++_id`,
};

export const version50Store = {
  requirements: `&_id, label, processes`,
  requirementsService: `++_id`,
  installationBasis: `&_id, label, processes`,
  installationBasisService: `++_id`,
  constructionProductDocuments: `&_id, label, processes`,
  constructionProductDocumentsService: `++_id`,
  deviceDocuments: `&_id, label, processes`,
  deviceDocumentsService: `++_id`,
  additionalDocuments: `&_id, label, processes`,
  additionalDocumentsService: `++_id`,
};

export const version51Store = {
  issues: `&_id, deleted, stage, inspection`,
};

export const version45Store = {
  issueWorkflows: `&_id, name`,
  issueWorkflowsService: `++_id`,
};

export const version46Store = {
  documents: `++localId, &_id, issueId, eventId, inspectionId, templateId, checklistId, sectionId, protocolId, incomingEmailMessageId`,
};

export const version46Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  await transaction
    .table('documents')
    .toCollection()
    .modify((document: DocumentEntity) => {
      document.sectionId = document.sectionId || '';
    });
};

export const version47Upgrade = async (
  transaction: Transaction
): Promise<any> => {
  const documents: DocumentInsertEntity[] = [];

  await transaction
    .table('inspectionTemplates')
    .toCollection()
    .modify((template: any) => {
      if (!Array.isArray(template.sections)) {
        return;
      }

      template.sections.forEach((section: any) => {
        if (!section?._id || !Array.isArray(section.documents)) {
          return;
        }

        section.documents.forEach((document: DocumentInDto) => {
          documents.push(
            inDtoToDocumentInsertEntity(document, {
              templateId: template._id,
              sectionId: section._id,
            })
          );
        });

        section.documents = [];
      });
    });

  if (documents.length > 0) {
    await transaction.table('documents').bulkPut(documents);
  }
};

/**
 * PT-7117 (documentation versioning): the backend now takes a level's map as
 * `map` and an issue's location as `location` on a map version, and refuses
 * the old shapes. Queued requests are rewritten on the map each level showed
 * (read before its table is cleared); levels, issues and documentations are
 * downloaded again in the new shape, keeping what still waits to be sent.
 */
export const version52Upgrade = async (transaction: Transaction): Promise<void> => {
  const levelMaps = new Map<string, LocalizedOnMap>();
  await transaction.table('levels').each((level: { _id: string; maps?: unknown }) => {
    const map = pickLegacyLevelMap(level.maps);
    if (map) {
      levelMaps.set(level._id, map);
    }
  });
  const issueLevels = new Map<string, string>();
  await transaction.table('issues').each((issue: { _id?: string; primaryData?: { level?: string } }) => {
    if (issue._id && issue.primaryData?.level) {
      issueLevels.set(issue._id, issue.primaryData.level);
    }
  });
  const context: LegacyRequestContext = {
    findLevelMap: (levelId) => levelMaps.get(levelId),
    findIssueLevel: (issueId) => issueLevels.get(issueId),
  };

  const droppedLocations: string[] = [];
  await transaction
    .table('httpRequests')
    .toCollection()
    .modify((request: QueuedRequest, ref: { value: QueuedRequest }) => {
      const rewrite = rewriteLegacyQueuedRequest(request, context);
      ref.value = rewrite.request;
      if (rewrite.droppedLocationOfIssue) {
        droppedLocations.push(rewrite.droppedLocationOfIssue);
      }
    });
  if (droppedLocations.length > 0) {
    swLog('Migration 52: queued issue locations dropped, their level has no map', LogLevel.INFO, null, {
      entityName: 'issues',
      query: { issueIds: droppedLocations },
    });
  }

  await transaction.table('levels').clear();
  await transaction.table('issues').clear();
  // As the issues' own clear does: the documents of issues that wait to be sent stay.
  await transaction
    .table('documents')
    .filter((document: DocumentEntity) => Boolean(document.issueId) && isDocumentSettled(document))
    .delete();
  await transaction
    .table('documentations')
    .filter((documentation: DocumentationEntity) => !hasChangesToSend(documentation))
    .delete();
  await Promise.all(
    ['levelsService', 'issuesService', 'documentationsService'].map((table) => transaction.table(table).clear())
  );
};
